// Own shifts (architecture sprint-13 §14.1, §15). The open shift is NOT polled separately: GET /api/timer
// carries it, and useTimer writes it into ['shifts', 'current'] (see setShiftSnapshot), so the 30 s timer
// poll (hidden tabs too) and the cross-tab sync deliver it. Start / End write the cache, broadcast
// 'shift-changed' and invalidate ['timer'], ['shifts'] and ['entries'].
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { LocalDate } from '@shared/calendar';
import { apiRequest } from '@/lib/api';
import { LIVE_QUERY_OPTIONS } from '@/lib/live-query';
import { broadcast } from '@/lib/sync';
import type { MyShiftsDto, ShiftDto, ShiftLocationValue, ShiftNoticeDto, ShiftSnapshot, StartOptionDto } from '@/types/shifts';

export const SHIFTS_KEY = ['shifts'] as const;
export const SHIFT_CURRENT_KEY = ['shifts', 'current'] as const;

export function setShiftSnapshot(queryClient: QueryClient, shift: ShiftDto | null, notice: ShiftNoticeDto | null, serverNow?: string): void {
  const snapshot: ShiftSnapshot = {
    shift,
    notice,
    receivedAt: performance.now(),
    serverNowMs: serverNow ? Date.parse(serverNow) : Date.now(),
  };
  queryClient.setQueryData<ShiftSnapshot>(SHIFT_CURRENT_KEY, snapshot);
}

/** The cached open shift and notice; undefined until the first GET /api/timer answered. */
export function useActiveShift(): ShiftSnapshot | undefined {
  const { data } = useQuery<ShiftSnapshot>({
    queryKey: SHIFT_CURRENT_KEY,
    // Never fetched on its own: useTimer's query fills it (enabled: false keeps this a cache read).
    queryFn: () => Promise.reject(new Error('the open shift is loaded with GET /api/timer')),
    enabled: false,
    staleTime: Infinity,
  });
  return data;
}

/** Start options for the Dashboard card and the phone "+" sheet. Silent: the card simply hides on error. */
export function useStartOptions(enabled = true): UseQueryResult<StartOptionDto[]> {
  return useQuery({
    queryKey: [...SHIFTS_KEY, 'start-options'],
    queryFn: async () => (await apiRequest<{ items: StartOptionDto[] }>('/api/shifts/start-options')).items,
    enabled,
    meta: { silent: true },
    ...LIVE_QUERY_OPTIONS,
  });
}

export function useMyShifts(from: LocalDate, to: LocalDate): UseQueryResult<MyShiftsDto> {
  return useQuery({
    queryKey: [...SHIFTS_KEY, 'mine', from, to],
    queryFn: () => apiRequest<MyShiftsDto>(`/api/shifts/mine?from=${from}&to=${to}`),
  });
}

async function afterShiftChange(queryClient: QueryClient): Promise<void> {
  broadcast({ type: 'shift-changed' });
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['timer'] }),
    queryClient.invalidateQueries({ queryKey: [...SHIFTS_KEY, 'start-options'] }),
    queryClient.invalidateQueries({ queryKey: [...SHIFTS_KEY, 'mine'] }),
    queryClient.invalidateQueries({ queryKey: ['entries'] }),
  ]);
}

export interface StartShiftBody {
  projectId: string;
  location?: ShiftLocationValue;
}

/** Errors are mapped by the caller (meta.silent). */
export function useStartShift(): UseMutationResult<{ shift: ShiftDto; alreadyStarted: boolean; serverNow?: string }, Error, StartShiftBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiRequest<{ shift: ShiftDto; alreadyStarted: boolean; serverNow?: string }>('/api/shifts/start', { method: 'POST', body: JSON.stringify(body) }),
    meta: { silent: true },
    onSuccess: async ({ shift, serverNow }) => {
      setShiftSnapshot(queryClient, shift, null, serverNow);
      await afterShiftChange(queryClient);
    },
  });
}

export function useEndShift(): UseMutationResult<{ shift: ShiftDto; alreadyEnded: boolean }, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shiftId) => apiRequest<{ shift: ShiftDto; alreadyEnded: boolean }>(`/api/shifts/${shiftId}/end`, { method: 'POST', body: '{}' }),
    meta: { silent: true },
    onSuccess: async () => {
      setShiftSnapshot(queryClient, null, null);
      await afterShiftChange(queryClient);
    },
  });
}
